import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSend, MdDownload, MdPerson, MdZoomIn, MdClose, MdOpenInNew } from 'react-icons/md';
import { helpdeskTicketApi, helpdeskLookupApi } from '../../../api/helpdeskApi';
import axiosInstance from '../../../api/axiosInstance';
import { toast } from 'react-toastify';
import '../Admin.css';
import './HelpdeskTicketDetail.css';

const STATUS_OPTIONS = ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];

const ID_CARD_STATUS_OPTIONS = [
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'DATA_VERIFIED', label: 'Data Verified' },
  { value: 'PHOTO_APPROVED', label: 'Photo Approved' },
  { value: 'PRINT_QUEUE', label: 'Print Queue' },
  { value: 'PRINTING', label: 'Printing' },
  { value: 'PRINT_READY', label: 'Print Ready' },
  { value: 'DELIVERED', label: 'Delivered to User' },
  { value: 'REJECTED', label: 'Rejected' },
];

function HelpdeskTicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [assignToId, setAssignToId] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [newIdCardStatus, setNewIdCardStatus] = useState('');
  const [idCardHistory, setIdCardHistory] = useState([]);
  const [requesterRecentTickets, setRequesterRecentTickets] = useState([]);
  const [requesterTotalTicketCount, setRequesterTotalTicketCount] = useState(0);
  const [showAllRequesterTickets, setShowAllRequesterTickets] = useState(false);
  const [allRequesterTickets, setAllRequesterTickets] = useState([]);
  const [loadingAllTickets, setLoadingAllTickets] = useState(false);

  const loadTicket = useCallback(async () => {
    try {
      const [responseData, commentsData] = await Promise.all([
        helpdeskTicketApi.getById(ticketId),
        helpdeskTicketApi.getComments(ticketId),
      ]);
      // Response may be { ticket, idCardHistory } or just the ticket object
      const ticketData = responseData?.ticket || responseData;
      setTicket(ticketData);
      if (responseData?.idCardHistory) {
        setIdCardHistory(responseData.idCardHistory);
      }
      if (responseData?.requesterRecentTickets) {
        setRequesterRecentTickets(responseData.requesterRecentTickets);
      }
      if (responseData?.requesterTotalTicketCount != null) {
        setRequesterTotalTicketCount(responseData.requesterTotalTicketCount);
      }
      setComments(Array.isArray(commentsData) ? commentsData : []);
      setNewStatus(ticketData?.ticketStatus || '');
      setNewIdCardStatus(ticketData?.idCardStatus || '');
      setAssignToId(ticketData?.assignedTo?.id || '');

      // Send both locationName (e.g. "CHN DLF -1") and city (e.g. "Chennai") so the
      // backend can match admins who store either granularity in their location field.
      const locationName = ticketData?.workLocation?.locationName || '';
      const locationCity = ticketData?.workLocation?.city || '';
      const adminsData = await helpdeskLookupApi.getAdminUsers(locationName, locationCity);
      setAdmins(Array.isArray(adminsData) ? adminsData : []);
    } catch (e) {
      toast.error('Failed to load ticket');
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  // Load photo/attachment previews for image files
  useEffect(() => {
    if (!ticket) return;
    let cancelled = false;

    const loadPhoto = async () => {
      if (ticket.photoName) {
        try {
          const response = await axiosInstance.get(
            `/Corrohealth/helpdesk/tickets/${ticket.id}/photo`,
            { responseType: 'arraybuffer' }
          );
          if (!cancelled && response.data) {
            const contentType = response.headers['content-type'] || ticket.photoContentType || 'image/jpeg';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            setPhotoUrl(url);
          }
        } catch (e) { console.error('Photo load error:', e); }
      }
    };

    const loadAttachmentPreview = async () => {
      const ct = ticket.attachmentContentType || '';
      if (ticket.attachmentName && ct.startsWith('image/')) {
        try {
          const response = await axiosInstance.get(
            `/Corrohealth/helpdesk/tickets/${ticket.id}/attachment`,
            { responseType: 'arraybuffer' }
          );
          if (!cancelled && response.data) {
            const contentType = response.headers['content-type'] || ct || 'image/jpeg';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            setAttachmentPreviewUrl(url);
          }
        } catch (e) { console.error('Attachment load error:', e); }
      }
    };

    loadPhoto();
    loadAttachmentPreview();

    return () => {
      cancelled = true;
      if (photoUrl) window.URL.revokeObjectURL(photoUrl);
      if (attachmentPreviewUrl) window.URL.revokeObjectURL(attachmentPreviewUrl);
    };
  }, [ticket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssign = async () => {
    if (!assignToId) {
      toast.warning('Please select a user to assign');
      return;
    }
    try {
      await helpdeskTicketApi.assignTicket(ticketId, { assignToUserId: assignToId });
      toast.success('Ticket assigned successfully');
      loadTicket();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to assign ticket');
    }
  };

  const handleStatusChange = async () => {
    if (!ticket?.assignedTo?.id && !assignToId) {
      toast.warning('Please assign the ticket to a user before updating the status');
      return;
    }
    if (!newStatus || newStatus === ticket?.ticketStatus) {
      toast.warning('Please select a different status');
      return;
    }
    try {
      // If an assignee is selected in the dropdown but not yet saved, persist it first
      if (assignToId && assignToId !== ticket?.assignedTo?.id) {
        await helpdeskTicketApi.assignTicket(ticketId, { assignToUserId: assignToId });
      }
      await helpdeskTicketApi.updateStatus(ticketId, { status: newStatus, comment: statusComment });
      toast.success('Status updated successfully');
      setStatusComment('');
      loadTicket();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update status');
    }
  };

  const handleIdCardStatusChange = async () => {
    if (!newIdCardStatus || newIdCardStatus === ticket?.idCardStatus) {
      toast.warning('Please select a different ID card status');
      return;
    }
    try {
      await helpdeskTicketApi.updateIdCardStatus(ticketId, newIdCardStatus);
      toast.success('ID card status updated');
      loadTicket();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update ID card status');
    }
  };

  const handleViewAllRequesterTickets = async () => {
    if (showAllRequesterTickets) {
      setShowAllRequesterTickets(false);
      return;
    }
    if (!ticket?.requester?.id) return;
    setLoadingAllTickets(true);
    try {
      const tickets = await helpdeskTicketApi.getByRequester(ticket.requester.id);
      setAllRequesterTickets(Array.isArray(tickets) ? tickets.filter(t => t.id !== ticket.id) : []);
      setShowAllRequesterTickets(true);
    } catch (e) {
      toast.error('Failed to load tickets');
    } finally {
      setLoadingAllTickets(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await helpdeskTicketApi.addComment(ticketId, { commentText: newComment, internal: isInternal });
      toast.success('Comment added');
      setNewComment('');
      setIsInternal(false);
      loadTicket();
    } catch (e) {
      toast.error('Failed to add comment');
    }
  };

  const sanitizeFilename = (name) => {
    if (!name) return 'download';
    return name.replace(/[/\\:*?"<>|\r\n]/g, '_').replace(/^\.+/, '').substring(0, 255);
  };

  const handleDownload = async (type) => {
    try {
      const response = type === 'attachment'
        ? await helpdeskTicketApi.downloadAttachment(ticketId)
        : await helpdeskTicketApi.downloadPhoto(ticketId);

      const contentDisposition = response.headers['content-disposition'];
      let filename = type === 'attachment' ? ticket?.attachmentName : ticket?.photoName;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', sanitizeFilename(filename));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Failed to download ${type}`);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
      year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      OPEN: '#2196F3', ASSIGNED: '#FF9800', IN_PROGRESS: '#9C27B0',
      RESOLVED: '#4CAF50', CLOSED: '#607D8B', REOPENED: '#F44336'
    };
    return colors[status] || '#666';
  };

  if (isLoading) return <div className="helpdesk-ticket-detail"><div className="loading">Loading ticket...</div></div>;
  if (!ticket) return <div className="helpdesk-ticket-detail"><div className="not-found">Ticket not found</div></div>;

  return (
    <div className="helpdesk-ticket-detail">
      <div className="detail-header">
        <button className="btn btn-back" onClick={() => navigate('/admin/helpdesk/dashboard')}>
          <MdArrowBack /> Back to Dashboard
        </button>
        <div className="ticket-header-info">
          <h2>{ticket.ticketNumber}</h2>
          <span className="status-badge" style={{ background: getStatusColor(ticket.ticketStatus) + '20', color: getStatusColor(ticket.ticketStatus) }}>
            {ticket.ticketStatus?.replace('_', ' ')}
          </span>
          {ticket.requestType === 'ID_CARD_REQUEST' && ticket.idCardStatus && (
            <span className={`id-card-badge id-card-badge-${ticket.idCardStatus?.toLowerCase()}`}>
              {ID_CARD_STATUS_OPTIONS.find(o => o.value === ticket.idCardStatus)?.label || ticket.idCardStatus}
            </span>
          )}
        </div>
      </div>

      <div className="detail-grid">
        {/* Ticket Info */}
        <div className="detail-card">
          <h3>Ticket Information</h3>
          <div className="info-grid">
            <div className="info-item"><span className="label">Request Type</span><span className="value">{ticket.requestType === 'GENERAL_REQUEST' ? 'General Request' : 'ID Card Request'}</span></div>
            <div className="info-item"><span className="label">Title</span><span className="value">{ticket.title || '-'}</span></div>
            <div className="info-item"><span className="label">Requested By</span><span className="value">{ticket.requester ? `${ticket.requester.firstName} ${ticket.requester.secondName}` : '-'}</span></div>
            {ticket.requester?.empId && <div className="info-item"><span className="label">Emp ID</span><span className="value"><code style={{ background: '#f3f0f7', color: '#512e6e', padding: '2px 8px', borderRadius: '6px', fontSize: '13px', fontWeight: 600 }}>{ticket.requester.empId}</code></span></div>}
            <div className="info-item"><span className="label">Created</span><span className="value">{formatDateTime(ticket.createdAt)}</span></div>
            {ticket.requestForType && <div className="info-item"><span className="label">Request For</span><span className="value">{ticket.requestForType}</span></div>}
            {ticket.onBehalfName && <div className="info-item"><span className="label">On Behalf Of</span><span className="value">{ticket.onBehalfName} ({ticket.onBehalfEmail})</span></div>}
            {ticket.category && <div className="info-item"><span className="label">Category</span><span className="value">{ticket.category.categoryName}</span></div>}
            {ticket.subCategory && <div className="info-item"><span className="label">Sub Category</span><span className="value">{ticket.subCategory.subCategoryName}</span></div>}
            {ticket.priority && <div className="info-item"><span className="label">Priority</span><span className="value priority-val">{ticket.priority}</span></div>}
            {ticket.city && <div className="info-item"><span className="label">City</span><span className="value">{ticket.city}</span></div>}
            {ticket.workLocation && <div className="info-item"><span className="label">Work Location</span><span className="value">{ticket.workLocation.locationName}</span></div>}
            {ticket.floor && <div className="info-item"><span className="label">Floor</span><span className="value">{ticket.floor}</span></div>}
            {ticket.cabinWorkstation && <div className="info-item"><span className="label">Cabin/Workstation</span><span className="value">{ticket.cabinWorkstation}</span></div>}
            {ticket.contactNumber && <div className="info-item"><span className="label">Contact</span><span className="value">{ticket.contactNumber}</span></div>}
            {ticket.description && <div className="info-item full-width"><span className="label">Description</span><span className="value">{ticket.description}</span></div>}

            {/* ID Card fields with photo on the right */}
            {ticket.requestType === 'ID_CARD_REQUEST' && (
              <>
                {ticket.employeeId && <div className="info-item"><span className="label">Employee ID</span><span className="value">{ticket.employeeId}</span></div>}
                {ticket.employeeName && <div className="info-item"><span className="label">Employee Name</span><span className="value">{ticket.employeeName}</span></div>}
                {ticket.employeeLocation && <div className="info-item"><span className="label">Employee Location</span><span className="value">{ticket.employeeLocation}</span></div>}
                {ticket.employeeDob && <div className="info-item"><span className="label">Date of Birth</span><span className="value">{ticket.employeeDob}</span></div>}
                {ticket.employeeDoj && <div className="info-item"><span className="label">Date of Joining</span><span className="value">{ticket.employeeDoj}</span></div>}
                {ticket.bloodGroup && <div className="info-item"><span className="label">Blood Group</span><span className="value">{ticket.bloodGroup}</span></div>}
                {ticket.emergencyContact && <div className="info-item"><span className="label">Emergency Contact</span><span className="value">{ticket.emergencyContact}</span></div>}
                {ticket.photoName && (
                  <div className="info-item idcard-photo-cell">
                    <span className="file-label">Photo</span>
                    {photoUrl ? (
                      <div className="image-preview-wrapper" onClick={() => setLightboxUrl(photoUrl)}>
                        <img src={photoUrl} alt="Employee Photo" className="inline-image-preview" />
                        <div className="image-zoom-overlay"><MdZoomIn /></div>
                      </div>
                    ) : (
                      <div className="image-loading">Loading...</div>
                    )}
                    <button className="btn btn-download" style={{ marginTop: '8px' }} onClick={() => handleDownload('photo')}>
                      <MdDownload /> {ticket.photoName}
                    </button>
                  </div>
                )}
                {ticket.residentialAddress && <div className="info-item full-width"><span className="label">Residential Address</span><span className="value">{ticket.residentialAddress}</span></div>}
              </>
            )}
          </div>

          {/* ID Card History Section */}
          {idCardHistory.length > 0 && (
            <div className="detail-section" style={{ marginTop: '20px' }}>
              <h3 style={{ marginBottom: '12px', color: '#333', fontSize: '16px' }}>ID Card History (from Legacy System)</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>VFS ID</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Emp ID</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Name</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>ID Card Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Blood Group</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Contact</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Emergency</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {idCardHistory.map((h, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px' }}>{h.vfsId}</td>
                        <td style={{ padding: '8px 12px' }}>{h.employeeId}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{h.employeeName}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: h.status === 'COMPLETED' ? '#ecfdf5' : h.status === 'PENDING' ? '#fffbeb' : '#f5f5f5', color: h.status === 'COMPLETED' ? '#065f46' : h.status === 'PENDING' ? '#92400e' : '#666' }}>
                            {h.status}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{h.idCardStatus || '-'}</td>
                        <td style={{ padding: '8px 12px' }}>{h.bloodGroup || '-'}</td>
                        <td style={{ padding: '8px 12px' }}>{h.contact || '-'}</td>
                        <td style={{ padding: '8px 12px' }}>{h.emergencyContact || '-'}</td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{h.legacyCreatedAt ? new Date(h.legacyCreatedAt).toLocaleDateString('en-IN') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Requester's Other Tickets */}
          {requesterRecentTickets.length > 0 && (
            <div className="detail-section" style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
                  Requester's Other Tickets ({requesterTotalTicketCount})
                </h3>
                {requesterTotalTicketCount > 10 && (
                  <button
                    type="button"
                    onClick={handleViewAllRequesterTickets}
                    style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
                  >
                    {loadingAllTickets ? 'Loading...' : showAllRequesterTickets ? 'Show Recent' : 'View All'}
                  </button>
                )}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Ticket #</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', color: '#333', fontWeight: 600 }}>Title</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Type</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Priority</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap', color: '#333', fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', width: '50px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllRequesterTickets ? allRequesterTickets : requesterRecentTickets).map((t, i) => (
                      <tr key={t.id || i} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={() => navigate(`/admin/helpdesk/tickets/${t.id}`)}>
                        <td style={{ padding: '8px 12px', fontWeight: 500, color: '#2563eb', whiteSpace: 'nowrap' }}>{t.ticketNumber}</td>
                        <td style={{ padding: '8px 12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title || '-'}</td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{t.requestType === 'ID_CARD_REQUEST' ? 'ID Card' : 'General'}</td>
                        <td style={{ padding: '8px 12px' }}>
                          {t.priority && (
                            <span className={`priority-badge priority-${t.priority?.toLowerCase()}`}>{t.priority}</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span className={`status-badge status-${(t.ticketStatus || '').toLowerCase()}`}>
                            {(t.ticketStatus || '').replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{t.createdAt ? formatDateTime(t.createdAt) : '-'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <MdOpenInNew style={{ color: '#2563eb', fontSize: '16px' }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Attachment/Photo Previews & Downloads */}
          {(ticket.attachmentName || (ticket.photoName && ticket.requestType !== 'ID_CARD_REQUEST')) && (
            <div className="file-section">
              {ticket.photoName && ticket.requestType !== 'ID_CARD_REQUEST' && (
                <div className="file-preview-block">
                  <span className="file-label">Photo</span>
                  {photoUrl ? (
                    <div className="image-preview-wrapper" onClick={() => setLightboxUrl(photoUrl)}>
                      <img src={photoUrl} alt="Photo" className="inline-image-preview" />
                      <div className="image-zoom-overlay"><MdZoomIn /></div>
                    </div>
                  ) : (
                    <div className="image-loading">Loading photo...</div>
                  )}
                  <button className="btn btn-download" onClick={() => handleDownload('photo')}>
                    <MdDownload /> {ticket.photoName}
                  </button>
                </div>
              )}
              {ticket.attachmentName && (
                <div className="file-preview-block">
                  <span className="file-label">Attachment</span>
                  {attachmentPreviewUrl && (
                    <div className="image-preview-wrapper" onClick={() => setLightboxUrl(attachmentPreviewUrl)}>
                      <img src={attachmentPreviewUrl} alt="Attachment" className="inline-image-preview" />
                      <div className="image-zoom-overlay"><MdZoomIn /></div>
                    </div>
                  )}
                  <button className="btn btn-download" onClick={() => handleDownload('attachment')}>
                    <MdDownload /> {ticket.attachmentName}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Admin Actions */}
        <div className="detail-card">
          <h3>Admin Actions</h3>

          <div className="action-section">
            <label>Assign To</label>
            <div className="action-row">
              <select value={assignToId} onChange={e => setAssignToId(e.target.value)}>
                <option value="">Select assignee...</option>
                {admins.map(a => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
              </select>
              <button className="btn btn-primary" onClick={handleAssign}><MdPerson /> Assign</button>
            </div>
            {ticket.assignedTo && (
              <div className="current-assignee">
                Currently assigned to: <strong>{ticket.assignedTo.firstName} {ticket.assignedTo.secondName}</strong>
              </div>
            )}
          </div>

          <div className="action-section">
            <label>Update Status</label>
            <div className="action-row">
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
              <button className="btn btn-primary" onClick={handleStatusChange}>Update</button>
            </div>
            <input
              type="text"
              placeholder="Optional comment for status change..."
              value={statusComment}
              onChange={e => setStatusComment(e.target.value)}
              className="status-comment-input"
            />
          </div>

          {/* ID Card Status — only for ID Card Request tickets */}
          {ticket.requestType === 'ID_CARD_REQUEST' && (
            <div className="action-section id-card-status-section">
              <label>ID Card Status</label>
              {ticket.idCardStatus && (
                <div className="current-id-card-status">
                  Current: <span className={`id-card-badge id-card-badge-${ticket.idCardStatus?.toLowerCase()}`}>{ID_CARD_STATUS_OPTIONS.find(o => o.value === ticket.idCardStatus)?.label || ticket.idCardStatus}</span>
                </div>
              )}
              <div className="action-row">
                <select value={newIdCardStatus} onChange={e => setNewIdCardStatus(e.target.value)}>
                  <option value="">Select status...</option>
                  {ID_CARD_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <button className="btn btn-primary" onClick={handleIdCardStatusChange}>Update</button>
              </div>
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="detail-card full-width">
          <h3>Comments</h3>
          <div className="comments-list">
            {comments.length === 0 ? (
              <div className="no-comments">No comments yet</div>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className={`comment-item ${comment.internal ? 'internal' : ''}`}>
                  <div className="comment-header">
                    <strong>{comment.commenterName}</strong>
                    <span className="comment-email">{comment.commenterEmail}</span>
                    {comment.internal && <span className="internal-badge">Internal</span>}
                    <span className="comment-date">{formatDateTime(comment.createdAt)}</span>
                  </div>
                  <div className="comment-text">{comment.commentText}</div>
                </div>
              ))
            )}
          </div>

          <div className="add-comment">
            <textarea
              placeholder="Add a comment..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              rows={3}
            />
            <div className="comment-actions">
              <label className="internal-checkbox">
                <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                Internal note (not visible to user)
              </label>
              <button className="btn btn-primary" onClick={handleAddComment} disabled={!newComment.trim()}>
                <MdSend /> Add Comment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="image-lightbox" onClick={() => setLightboxUrl(null)}>
          <button className="lightbox-close" onClick={() => setLightboxUrl(null)}><MdClose /></button>
          <img src={lightboxUrl} alt="Full size" className="lightbox-image" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

export default HelpdeskTicketDetail;
